import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";

export type AdminUserStatus = "active" | "invited" | "revoked" | "inactive";

export type AdminUserCategory = "internal" | null;

export type AdminUser = {
  email: string;
  status: AdminUserStatus;
  instanceCount: number;
  instanceBudget: number;
  instanceBudgetOverridden: boolean;
  flagOverrideCount: number;
  instanceMemberCap: number;
  instanceMemberCapOverridden: boolean;
  lastSeenAt: string | null;
  lastLoginAt: string | null;
  category: AdminUserCategory;
  loginDisabled: boolean;
  isAdmin: boolean;
};

export type DeleteUserResult =
  | { ok: true }
  | { ok: false; code: "USER_HAS_INSTANCES" | "SELF_DELETE" | "UNKNOWN"; message: string };

export type LoginToggleResult = { ok: boolean; message: string };

export type AdminUsersStatus = "loading" | "ready" | "error";

export type UseAdminUsersResult = {
  status: AdminUsersStatus;
  users: ReadonlyArray<AdminUser>;
  errorMessage: string | null;
  isFetching: boolean;
  refetch: () => Promise<void>;
  setBudget: (email: string, budget: number) => Promise<void>;
  clearBudget: (email: string) => Promise<void>;
  setInstanceMemberCap: (email: string, cap: number) => Promise<void>;
  clearInstanceMemberCap: (email: string) => Promise<void>;
  setCategory: (email: string, category: "internal") => Promise<void>;
  clearCategory: (email: string) => Promise<void>;
  deleteUser: (email: string) => Promise<DeleteUserResult>;
  disableLogin: (email: string) => Promise<LoginToggleResult>;
  enableLogin: (email: string) => Promise<LoginToggleResult>;
};

const LIST_URL = "/api/admin/users";

const fetchUsers = async (signal: AbortSignal): Promise<ReadonlyArray<AdminUser>> => {
  const res = await fetch(LIST_URL, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
  return (await res.json()) as ReadonlyArray<AdminUser>;
};

export const useAdminUsers = (): UseAdminUsersResult => {
  const { status, data, error, isFetching, refetch } = useCachedQuery<ReadonlyArray<AdminUser>>({
    cacheResource: ADMIN_QUERIES.users.resource,
    queryKey: LIST_URL,
    fetcher: fetchUsers,
  });
  const users = data ?? [];
  const errorMessage = error;

  const setBudget = useCallback(
    async (email: string, budget: number): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/instance-budget`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ budget }),
        },
      );
      if (!res.ok) throw new Error(`set budget failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const clearBudget = useCallback(
    async (email: string): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/instance-budget`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        },
      );
      if (!res.ok) throw new Error(`clear budget failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const setInstanceMemberCap = useCallback(
    async (email: string, cap: number): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/instance-member-cap`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ cap }),
        },
      );
      if (!res.ok) throw new Error(`set instance member cap failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const clearInstanceMemberCap = useCallback(
    async (email: string): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/instance-member-cap`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        },
      );
      if (!res.ok) throw new Error(`clear instance member cap failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const setCategory = useCallback(
    async (email: string, category: "internal"): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/category`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ category }),
        },
      );
      if (!res.ok) throw new Error(`set category failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const clearCategory = useCallback(
    async (email: string): Promise<void> => {
      const res = await fetch(
        `${LIST_URL}/${encodeURIComponent(email)}/category`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        },
      );
      if (!res.ok) throw new Error(`clear category failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const deleteUser = useCallback(
    async (email: string): Promise<DeleteUserResult> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.ok) {
          await refetch();
          return { ok: true };
        }
        const body = (await res.json().catch(() => null)) as
          | { code?: string; message?: string }
          | null;
        const code =
          body?.code === "USER_HAS_INSTANCES" || body?.code === "SELF_DELETE"
            ? body.code
            : "UNKNOWN";
        return { ok: false, code, message: body?.message ?? `Delete failed (${res.status})` };
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : "Network error" };
      }
    },
    [refetch],
  );

  const toggleLogin = useCallback(
    async (email: string, action: "disable" | "enable"): Promise<LoginToggleResult> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}/${action}`, {
          method: "POST",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.ok) {
          await refetch();
          return { ok: true, message: "" };
        }
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        return { ok: false, message: body?.message ?? `${action} login failed (${res.status})` };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : "Network error" };
      }
    },
    [refetch],
  );

  const disableLogin = useCallback(
    (email: string): Promise<LoginToggleResult> => toggleLogin(email, "disable"),
    [toggleLogin],
  );

  const enableLogin = useCallback(
    (email: string): Promise<LoginToggleResult> => toggleLogin(email, "enable"),
    [toggleLogin],
  );

  return {
    status, users, errorMessage, isFetching, refetch, setBudget, clearBudget,
    setInstanceMemberCap, clearInstanceMemberCap,
    setCategory, clearCategory, deleteUser, disableLogin, enableLogin,
  };
};
